import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, X, ShoppingBag, ArrowRight, ArrowLeft } from "lucide-react";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { toast } from "../components/ui/sonner";
import { SmartImage } from "../components/ui/SmartImage";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/filters";

const WishCard = ({ item, remove, moveToCart }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.96 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`wishlist-item-${item.id}`}
    className="group flex flex-col"
  >
    <div className="relative overflow-hidden rounded-sm bg-stone spotlight">
      <Link to={`/vanities/${item.id}`} className="block aspect-[4/5] overflow-hidden">
        <SmartImage
          src={item.image}
          alt={item.name}
          label={item.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
        />
      </Link>
      <button
        onClick={() => remove(item.id)}
        data-testid={`wishlist-remove-${item.id}`}
        aria-label="Remove from wishlist"
        className="absolute top-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-ivory/90 backdrop-blur-sm text-charcoal transition-transform duration-300 hover:scale-110"
      >
        <X size={16} strokeWidth={2} />
      </button>
    </div>

    <div className="pt-4 flex items-start justify-between gap-3">
      <h3 className="font-serif text-xl md:text-2xl text-charcoal leading-tight">
        <Link to={`/vanities/${item.id}`} className="hover:text-taupe transition-colors">
          {item.name}
        </Link>
      </h3>
      <p className="font-serif text-lg md:text-xl text-charcoal whitespace-nowrap">
        {formatPrice(item.price)}
      </p>
    </div>

    <div className="mt-3 flex flex-wrap gap-1.5">
      {[item.mounting, `${item.width}" Width`, item.sink, item.style].map((m) => (
        <span key={m} className="text-[0.68rem] text-charcoal/70 bg-stone/70 border border-beige/70 px-2 py-1 rounded-full">
          {m}
        </span>
      ))}
    </div>

    <button
      onClick={() => moveToCart(item)}
      data-testid={`wishlist-move-${item.id}`}
      className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-charcoal text-ivory py-3 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
    >
      <ShoppingBag size={15} strokeWidth={1.6} /> Move to Cart
    </button>
  </motion.div>
);

export default function Wishlist() {
  const { items, remove, clear } = useWishlist();
  const { addItem } = useCart();
  const hasItems = items.length > 0;

  const moveToCart = (item) => {
    addItem({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image,
      mounting: item.mounting,
      style: item.style,
      width: item.width,
      finish: item.finish,
      sink: item.sink,
    });
    remove(item.id);
    toast.success(`${item.name} moved to cart`);
  };

  return (
    <div data-testid="wishlist-page" className="bg-ivory min-h-screen">
      <Header />

      <main className="pt-28 md:pt-32 pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-12">
            <div>
              <h1 className="font-serif font-light text-charcoal text-4xl md:text-6xl leading-[1.02] tracking-tight">
                Wishlist
              </h1>
              {hasItems && (
                <p className="mt-3 eyebrow text-taupe">
                  {items.length} {items.length === 1 ? "saved vanity" : "saved vanities"}
                </p>
              )}
            </div>
            {hasItems && (
              <button onClick={clear} data-testid="wishlist-clear" className="text-sm text-taupe link-sweep w-fit">
                Clear all
              </button>
            )}
          </div>

          {!hasItems ? (
            <div data-testid="wishlist-empty" className="text-center py-20 flex flex-col items-center">
              <span className="grid place-items-center h-20 w-20 rounded-full bg-stone">
                <Heart size={30} strokeWidth={1.2} className="text-taupe" />
              </span>
              <h2 className="font-serif font-light text-charcoal text-4xl md:text-5xl mt-8">
                Nothing saved yet
              </h2>
              <p className="mt-4 max-w-md text-charcoal/65 leading-relaxed">
                Tap the heart on any vanity to save it here and compare your favourites side by side.
              </p>
              <Link
                to="/vanities"
                data-testid="wishlist-shop-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-charcoal text-ivory px-8 py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
              >
                Explore Vanities <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                <AnimatePresence mode="popLayout">
                  {items.map((item) => (
                    <WishCard key={item.id} item={item} remove={remove} moveToCart={moveToCart} />
                  ))}
                </AnimatePresence>
              </div>
              <div className="mt-14">
                <Link to="/vanities" data-testid="wishlist-continue" className="inline-flex items-center gap-2 text-sm font-medium text-charcoal link-sweep">
                  <ArrowLeft size={15} strokeWidth={1.5} /> Continue shopping
                </Link>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
